/**
 * Taak Form Modal (Epic C-1 — Fundament, Story C-1.3, UX-C2; uitgebreid in
 * Story C-2.3 met het "Vereiste kwalificatie"-veld)
 *
 * Custom-modal-formulier (aanmaken + bewerken), zelfde overlay +
 * Escape-to-close-patroon als `UitDienstDialog.tsx` (dit project heeft geen
 * shadcn/ui — GEEN `@radix-ui/react-dialog` toevoegen).
 *
 * `TaskFormFields` is een aparte sub-sectie (Kritieke notities, Story
 * C-1.3): Story C-2.3 breidt die hier uit met het vierde form-blok, zonder
 * de component te herstructureren.
 */
import { useEffect, useState } from 'react';
import {
  PLANNING_TASK_ACTIVITIES,
  PLANNING_TASK_ACTIVITY_LABELS,
  activityUsesTargetMarket,
  isQualificationTripleComplete,
} from '@functieloonhuis/shared';
import { useCreateTask, useUpdateTask } from '../hooks/usePlanningTasks';
import { useQualificationListItems } from '../hooks/useQualificationLists';
import { ColorSwatchPicker } from './ColorSwatchPicker';
import { ApiRequestError } from '../../../lib/api';
import type { PlanningTaskActivity, PlanningTaskDTO, QualificationListItemDTO } from '@functieloonhuis/shared';

const NAME_REQUIRED_MESSAGE = 'Naam is verplicht.';
const NAME_TAKEN_MESSAGE = 'Er bestaat al een actieve taak met deze naam.';
const TRIPLE_INCOMPLETE_MESSAGE =
  'Activiteit, productcategorie en doelmarkt moeten alle drie ingevuld zijn, of allemaal leeg.';

interface TaakFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** `null`/`undefined` = aanmaken; een taak = bewerken. */
  task?: PlanningTaskDTO | null;
}

/**
 * Story C-2.3 (AC 3) — de select biedt normaliter alleen ACTIEVE lijst-
 * items aan, maar een bestaande triple kan naar een inmiddels gearchiveerd
 * item verwijzen. Dat item wordt dan alsnog toegevoegd (gedempt/`disabled`,
 * dus wel zichtbaar/geselecteerd, niet nieuw kiesbaar) — anders zou het
 * formulier de huidige waarde stil laten vallen.
 */
function buildTripleSelectOptions(
  activeItems: QualificationListItemDTO[],
  allItemsIncludingArchived: QualificationListItemDTO[],
  currentId: number | ''
): QualificationListItemDTO[] {
  if (currentId === '' || activeItems.some((item) => item.id === currentId)) {
    return activeItems;
  }
  const archivedCurrent = allItemsIncludingArchived.find((item) => item.id === currentId);
  return archivedCurrent ? [...activeItems, archivedCurrent] : activeItems;
}

interface TaskFormFieldsProps {
  name: string;
  onNameChange: (value: string) => void;
  description: string;
  onDescriptionChange: (value: string) => void;
  color: string | null;
  onColorChange: (hex: string | null) => void;
  nameError: string | null;
  requiredActivity: PlanningTaskActivity | '';
  onRequiredActivityChange: (value: PlanningTaskActivity | '') => void;
  requiredProductCategoryId: number | '';
  onRequiredProductCategoryIdChange: (value: number | '') => void;
  requiredTargetMarketId: number | '';
  onRequiredTargetMarketIdChange: (value: number | '') => void;
  tripleError: string | null;
}

function TaskFormFields({
  name,
  onNameChange,
  description,
  onDescriptionChange,
  color,
  onColorChange,
  nameError,
  requiredActivity,
  onRequiredActivityChange,
  requiredProductCategoryId,
  onRequiredProductCategoryIdChange,
  requiredTargetMarketId,
  onRequiredTargetMarketIdChange,
  tripleError,
}: TaskFormFieldsProps) {
  const { data: activeProductCategories } = useQualificationListItems('product-categories');
  const { data: allProductCategories } = useQualificationListItems('product-categories', true);
  const { data: activeTargetMarkets } = useQualificationListItems('target-markets');
  const { data: allTargetMarkets } = useQualificationListItems('target-markets', true);

  const productCategoryOptions = buildTripleSelectOptions(
    activeProductCategories ?? [],
    allProductCategories ?? [],
    requiredProductCategoryId
  );
  const targetMarketOptions = buildTripleSelectOptions(activeTargetMarkets ?? [], allTargetMarkets ?? [], requiredTargetMarketId);

  return (
    <>
      <div className="mb-4">
        <label htmlFor="taak-naam-input" className="mb-1 block text-sm font-medium text-gray-700">
          Naam
        </label>
        <input
          id="taak-naam-input"
          data-testid="taak-naam-input"
          type="text"
          value={name}
          onChange={(e) => onNameChange(e.target.value)}
          aria-invalid={!!nameError}
          aria-describedby={nameError ? 'taak-naam-error' : undefined}
          className="w-full rounded-md border border-[#E2E8F0] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#54949E]"
        />
        {nameError && (
          <p id="taak-naam-error" data-testid="taak-naam-error" className="mt-1 text-xs text-[#D64545]">
            {nameError}
          </p>
        )}
      </div>

      <div className="mb-4">
        <label htmlFor="taak-omschrijving-input" className="mb-1 block text-sm font-medium text-gray-700">
          Omschrijving
        </label>
        <textarea
          id="taak-omschrijving-input"
          data-testid="taak-omschrijving-input"
          value={description}
          onChange={(e) => onDescriptionChange(e.target.value)}
          rows={3}
          className="w-full rounded-md border border-[#E2E8F0] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#54949E]"
        />
      </div>

      <div className="mb-6">
        <span className="mb-1 block text-sm font-medium text-gray-700">Kleur</span>
        <ColorSwatchPicker value={color} onChange={onColorChange} />
      </div>

      <div className="mb-6">
        <span className="mb-1 block text-sm font-medium text-gray-700">Vereiste kwalificatie (optioneel)</span>
        <p id="taak-vereiste-kwalificatie-helptekst" data-testid="taak-vereiste-kwalificatie-helptekst" className="mb-2 text-xs text-gray-500">
          Alleen ingedeelde medewerkers zónder deze kwalificatie krijgen een waarschuwing — indelen blijft altijd mogelijk.
        </p>
        <div
          className={`grid gap-2 ${
            requiredActivity !== '' && !activityUsesTargetMarket(requiredActivity)
              ? 'grid-cols-2'
              : 'grid-cols-3'
          }`}
        >
          <select
            data-testid="taak-vereiste-kwalificatie-activiteit-select"
            value={requiredActivity}
            onChange={(e) => onRequiredActivityChange(e.target.value as PlanningTaskActivity | '')}
            aria-describedby={tripleError ? 'taak-vereiste-kwalificatie-error' : undefined}
            className="rounded-md border border-[#E2E8F0] px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#54949E]"
          >
            <option value="">Geen</option>
            {PLANNING_TASK_ACTIVITIES.map((activity) => (
              <option key={activity} value={activity}>
                {PLANNING_TASK_ACTIVITY_LABELS[activity]}
              </option>
            ))}
          </select>
          <select
            data-testid="taak-vereiste-kwalificatie-productcategorie-select"
            value={requiredProductCategoryId}
            onChange={(e) => onRequiredProductCategoryIdChange(e.target.value ? Number(e.target.value) : '')}
            aria-describedby={tripleError ? 'taak-vereiste-kwalificatie-error' : undefined}
            className="rounded-md border border-[#E2E8F0] px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#54949E]"
          >
            <option value="">Geen</option>
            {productCategoryOptions.map((item) => (
              <option key={item.id} value={item.id} disabled={item.isArchived}>
                {item.name}
                {item.isArchived ? ' (gearchiveerd)' : ''}
              </option>
            ))}
          </select>
          {/* (Her)controle kent geen doelmarkt; het veld verdwijnt dan in
              plaats van een keuze te tonen die de server zou afwijzen. */}
          {(requiredActivity === '' || activityUsesTargetMarket(requiredActivity)) && (
            <select
              data-testid="taak-vereiste-kwalificatie-doelmarkt-select"
              value={requiredTargetMarketId}
              onChange={(e) => onRequiredTargetMarketIdChange(e.target.value ? Number(e.target.value) : '')}
              aria-describedby={tripleError ? 'taak-vereiste-kwalificatie-error' : undefined}
              className="rounded-md border border-[#E2E8F0] px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#54949E]"
            >
              <option value="">Geen</option>
              {targetMarketOptions.map((item) => (
                <option key={item.id} value={item.id} disabled={item.isArchived}>
                  {item.name}
                  {item.isArchived ? ' (gearchiveerd)' : ''}
                </option>
              ))}
            </select>
          )}
        </div>
        {tripleError && (
          <p id="taak-vereiste-kwalificatie-error" data-testid="taak-vereiste-kwalificatie-error" className="mt-1 text-xs text-[#D64545]">
            {tripleError}
          </p>
        )}
      </div>
    </>
  );
}

export function TaakFormModal({ isOpen, onClose, task }: TaakFormModalProps) {
  const createTask = useCreateTask();
  const updateTask = useUpdateTask();
  const isEdit = !!task;
  const isPending = createTask.isPending || updateTask.isPending;

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [color, setColor] = useState<string | null>(null);
  const [requiredActivity, setRequiredActivity] = useState<PlanningTaskActivity | ''>('');
  const [requiredProductCategoryId, setRequiredProductCategoryId] = useState<number | ''>('');
  const [requiredTargetMarketId, setRequiredTargetMarketId] = useState<number | ''>('');
  const [saveAttempted, setSaveAttempted] = useState(false);
  const [nameConflictError, setNameConflictError] = useState<string | null>(null);
  const [tripleServerError, setTripleServerError] = useState<string | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setName(task?.name ?? '');
    setDescription(task?.description ?? '');
    setColor(task?.color ?? null);
    setRequiredActivity(task?.requiredActivity ?? '');
    setRequiredProductCategoryId(task?.requiredProductCategoryId ?? '');
    // Een oudere taak kan nog een doelmarkt dragen bij een activiteit die er
    // geen kent. Die waarde ongewijzigd inladen zou het formulier
    // onopslaanbaar maken: het veld wordt niet gerenderd, maar de validatie
    // wijst er wel naar — een foutmelding over iets dat niet op het scherm
    // staat, waar de gebruiker alleen uitkomt door de activiteit heen en weer
    // te zetten.
    setRequiredTargetMarketId(
      task?.requiredActivity && !activityUsesTargetMarket(task.requiredActivity)
        ? ''
        : (task?.requiredTargetMarketId ?? '')
    );
    setSaveAttempted(false);
    setNameConflictError(null);
    setTripleServerError(null);
    setServerError(null);
  }, [isOpen, task]);

  useEffect(() => {
    if (!isOpen) return;
    const listener = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isPending) onClose();
    };
    document.addEventListener('keydown', listener);
    return () => document.removeEventListener('keydown', listener);
  }, [isOpen, onClose, isPending]);

  if (!isOpen) return null;

  const trimmedName = name.trim();
  const hasName = trimmedName.length > 0;
  const nameError = nameConflictError ?? (saveAttempted && !hasName ? NAME_REQUIRED_MESSAGE : null);

  // Story C-2.3 (AC 2) — alles-of-niets. Sinds 2026-08-03 hangt "compleet" af
  // van de activiteit (bij (her)controle hóórt de doelmarkt leeg te zijn), dus
  // geen eigen telling meer: exact dezelfde predicate als de server, anders
  // keurt de een goed wat de ander afwijst.
  const tripleIncomplete = !isQualificationTripleComplete({
    requiredActivity: requiredActivity === '' ? null : requiredActivity,
    requiredProductCategoryId: requiredProductCategoryId === '' ? null : requiredProductCategoryId,
    requiredTargetMarketId: requiredTargetMarketId === '' ? null : requiredTargetMarketId,
  });
  const tripleError = tripleServerError ?? (saveAttempted && tripleIncomplete ? TRIPLE_INCOMPLETE_MESSAGE : null);

  /**
   * Kiest de vereiste activiteit en wist de doelmarkt zodra die niet meer
   * bestaat voor de nieuwe keuze.
   *
   * Zonder het wissen blijft een eerder gekozen doelmarkt in de state staan
   * terwijl de keuzelijst verdwijnt; het formulier zou dan onopgeloste
   * validatiefouten geven op een veld dat niet meer te zien is.
   */
  function kiesVereisteActiviteit(next: PlanningTaskActivity | '') {
    setRequiredActivity(next);
    if (next === '' || !activityUsesTargetMarket(next)) {
      setRequiredTargetMarketId('');
    }
  }

  async function handleSubmit() {
    if (isPending) return;
    setSaveAttempted(true);
    setNameConflictError(null);
    setTripleServerError(null);
    setServerError(null);
    if (!hasName || tripleIncomplete) return;

    const payload = {
      name: trimmedName,
      description: description.trim() || null,
      color,
      requiredActivity: requiredActivity === '' ? null : requiredActivity,
      requiredProductCategoryId: requiredProductCategoryId === '' ? null : requiredProductCategoryId,
      requiredTargetMarketId: requiredTargetMarketId === '' ? null : requiredTargetMarketId,
    };

    try {
      if (isEdit && task) {
        await updateTask.mutateAsync({ id: task.id, data: payload });
      } else {
        await createTask.mutateAsync(payload);
      }
      onClose();
    } catch (error) {
      if (error instanceof ApiRequestError && error.code === 'TASK_NAME_TAKEN') {
        setNameConflictError(NAME_TAKEN_MESSAGE);
      } else if (error instanceof ApiRequestError && error.code === 'QUALIFICATION_TRIPLE_INCOMPLETE') {
        setTripleServerError(TRIPLE_INCOMPLETE_MESSAGE);
      } else if (error instanceof ApiRequestError) {
        setServerError(error.message);
      } else {
        setServerError('Er is een onbekende fout opgetreden');
      }
    }
  }

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40" data-testid="taak-form-modal-overlay">
      <div
        role="dialog"
        aria-modal="true"
        data-testid="taak-form-modal"
        className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-lg bg-white p-6 shadow-xl"
      >
        <h2 className="mb-4 text-lg font-medium text-gray-900">{isEdit ? 'Taak bewerken' : 'Nieuwe taak'}</h2>

        {serverError && (
          <div data-testid="taak-form-server-error" className="mb-4 rounded-md bg-[#D64545]/10 px-3 py-2 text-sm text-[#D64545]">
            {serverError}
          </div>
        )}

        <TaskFormFields
          name={name}
          onNameChange={setName}
          description={description}
          onDescriptionChange={setDescription}
          color={color}
          onColorChange={setColor}
          nameError={nameError}
          requiredActivity={requiredActivity}
          onRequiredActivityChange={kiesVereisteActiviteit}
          requiredProductCategoryId={requiredProductCategoryId}
          onRequiredProductCategoryIdChange={setRequiredProductCategoryId}
          requiredTargetMarketId={requiredTargetMarketId}
          onRequiredTargetMarketIdChange={setRequiredTargetMarketId}
          tripleError={tripleError}
        />

        <div className="flex justify-end gap-2">
          <button
            type="button"
            data-testid="taak-form-cancel"
            onClick={onClose}
            disabled={isPending}
            className="rounded-md border border-[#E2E8F0] px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Annuleren
          </button>
          <button
            type="button"
            data-testid="taak-form-submit"
            onClick={handleSubmit}
            disabled={isPending}
            className="rounded-md bg-[#2F5A7A] px-4 py-2 text-sm font-medium text-white hover:bg-[#2F5A7A]/90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isPending ? 'Bezig...' : 'Opslaan'}
          </button>
        </div>
      </div>
    </div>
  );
}
